import {useCallback, useContext, useEffect, useRef} from 'react';
import {SearchPageTypeContext} from './search';
import {DefaultRootState, useDispatch, useSelector} from 'react-redux';
import {
    fetchDocumentDetails,
    resetSearchStateParts,
    setCurrentPageNumberAction,
    setQueryFieldsAction,
    sortChangeAction,
    startSearchAction,
    switchResultDetailsTabAction
} from './duck/search-actions';
import {
    ActiveDrawerContent,
    HibuSearchRequestSort,
    ProdDocument,
    SearchRequest,
    SearchSource,
    SortType,
    TabType
} from './search-types';
import {SearchPageType} from './search-config';
import {useLocation, useParams, useSearchParams} from 'react-router-dom';
import {doGetAppVersionsRequest, doLoadContainersRequest} from '../services/settings-service';
import {ReduxIndexedNormalizedType, State} from '../app-types';
import {HistoryEntry} from './duck/history-types';
import {SavedSearchWithId} from './save-search/duck/saveSearchComponent-types';
import {ContainersResponse} from '../settings/duck/settings-types';
import {parseSortFieldString} from './search-order-parser';
import {parseFiltersFromUrl} from './url/facet-filter-parser';
import {doFetchFacets, doSearchRequest, SEARCH_BATCH_SIZE} from '../services/search-service';
import {getSort, getSpellcheckParams} from './search.utils';
import {isSearchInBookmarks, isShowFieldSpecificSearchEnabled, isSpellcheckEnabled} from '../utils/utils';
import {useStateContainerName, useStateFieldSpecificSearch} from '../settings/duck/settings-hooks';
import {
    ClickableElement
} from './search-results/search-result-details/content/clickable-section-list/clickableSectionList';
import {PreviewDocument} from './search-results/search-preview/doc-preview-section/docPreviewRow-types';
import {parsePageNumberString} from './url/page-number-parser';
import {SearchCompletedAction} from './duck/search-types';
import {
    QUERY_FIELDS_ALL,
    QUERY_FIELDS_SEPARATOR,
    URL_FIELDS_PARAM,
    URL_FILTER_PARAM,
    URL_PAGE_PARAM,
    URL_QUERY_PARAM,
    URL_RF_PARAM,
    URL_SORT_PARAM,
    URL_TAB_PARAM
} from './const';
import {SEARCH_PATH} from '../config';
import {parseOpenTab} from './url/tab-type-parse';
import {getCurrentLanguage} from '../i18n-service';
import {AppDispatch} from '../store';
import {DynamicSize} from '../common/dragable-divider/dragableDivider';
import {useSettingsConfig} from '../config/config-hooks';
import {changeFilterPersistence, updateAllFilters} from '../facets/duck/filter.actions';
import {HibuFacetFilter} from '../facets/facet.types';

export function useSearchPageType(): SearchPageType {
    return useContext(SearchPageTypeContext);
}

/**
 * Returns the currently selected search result item as {@link ProdDocument} or {@code undefined}
 * if none is selected.
 */
export function useSelectedDocContext(): ProdDocument | undefined {
    const searchPageType = useSearchPageType();
    return useSelectedDocument(searchPageType);
}

const useSelectedDocument = (type: SearchPageType): ProdDocument | undefined => {
    return useSelector((state: DefaultRootState) => state.search[type].expandedProdDocument);
};

export function useStateExpandedProdDocumentId(): string | undefined {
    return useSelector((state: DefaultRootState) => state.search.BASIC.expandedProdDocument?.id);
}

export function useStateHistoryEntries(): HistoryEntry[] {
    const searchPageType = useSearchPageType();
    return useSelector((state: DefaultRootState) => state.search[searchPageType].historyEntries);
}

export function useStateSavedSearches(): SavedSearchWithId[] | undefined {
    const searchPageType = useSearchPageType();
    return useSelector((state: DefaultRootState) => state.search[searchPageType].savedSearches);
}

export function useStateSavedSearchesSharedByOthers(): SavedSearchWithId[] | undefined {
    const searchPageType = useSearchPageType();
    return useSelector((state: DefaultRootState) => state.search[searchPageType].savedSearchesSharedByOthers);
}

export function useStateShowScrollButton(): boolean {
    const searchPageType = useSearchPageType();
    return useSelector((state: DefaultRootState) => state.search[searchPageType].showScrollButton);
}

export function useStateShowFullDocument(): boolean {
    const searchPageType = useSearchPageType();
    return useSelector((state: DefaultRootState) => state.search[searchPageType].showFullDocument);
}

export function useStateDocs(): ReduxIndexedNormalizedType<ProdDocument> {
    const searchPageType = useSearchPageType();
    return useSelector((state: DefaultRootState) => state.search[searchPageType].docs);
}

export function useStateDocsCount(): number {
    const searchPageType = useSearchPageType();
    return useSelector((state: DefaultRootState) => state.search[searchPageType].docs.allKeys.length);
}

export function useStateQuery(): string {
    const searchPageType = useSearchPageType();
    return useSelector((state: DefaultRootState) => state.search[searchPageType].query);
}

export function useStateInDocumentQuery(): string | undefined {
    const searchPageType = useSearchPageType();
    return useSelector((state: DefaultRootState) => state.search[searchPageType].inDocumentQuery);
}

export function useStateLastSearchedQuery(): string {
    const searchPageType = useSearchPageType();
    return useSelector((state: DefaultRootState) => state.search[searchPageType].lastSearchedQuery);
}

export function useCurrentSortType(): SortType {
    const searchPageType = useSearchPageType();
    return useSelector((state: DefaultRootState) => state.search[searchPageType].currentSortType);
}

export function useDefaultSortType(): SortType | undefined {
    return useSelector((state: DefaultRootState) => state.search[SearchPageType.BASIC].defaultSortType);
}

export function getDefaultSortType(state: State): SortType | undefined {
    return state.search[SearchPageType.BASIC]?.defaultSortType;
}

export function useStateQueryFields(): string[] | undefined {
    const searchPageType = useSearchPageType();
    return useSelector((state: DefaultRootState) => state.search[searchPageType].queryFields);
}

export function useStateQueryPending(): boolean {
    const searchPageType = useSearchPageType();
    return useSelector((state: DefaultRootState) => state.search[searchPageType].queryPending);
}

export function useStateSpellCheckResult(): string {
    const searchPageType = useSearchPageType();
    return useSelector((state: DefaultRootState) => state.search[searchPageType].spellCheckResult);
}

export function useStateResultFor(): string {
    const searchPageType = useSearchPageType();
    return useSelector((state: DefaultRootState) => state.search[searchPageType].resultFor);
}

export function useStateSources(): SearchSource[] {
    const searchPageType = useSearchPageType();
    return useSelector((state: DefaultRootState) => state.search[searchPageType].sources);
}

export function useStateTotalFound(): number | null | undefined {
    const searchPageType = useSearchPageType();
    return useSelector((state: DefaultRootState) => state.search[searchPageType].totalFound);
}

export function useStateDocDetailsExpanded(): boolean {
    const searchPageType = useSearchPageType();
    return useSelector((state: DefaultRootState) => state.search[searchPageType].documentDetailsExpanded);
}

export function useStatePreviewDocument(): PreviewDocument | undefined {
    const searchPageType = useSearchPageType();
    return useSelector((state: DefaultRootState) => state.search[searchPageType].previewDocument);
}

export function useStatePreviewDocumentWithHighlights(): PreviewDocument | undefined {
    const searchPageType = useSearchPageType();
    return useSelector((state: DefaultRootState) => state.search[searchPageType].previewDocumentWithHighlights);
}

export function useStateDocumentQueryHighlights() {
    const searchPageType = useSearchPageType();
    return useSelector((state: DefaultRootState) => state.search[searchPageType].documentQueryHighlights);
}

export function useStateDocumentSelectedTags() {
    const searchPageType = useSearchPageType();
    return useSelector((state: DefaultRootState) => state.search[searchPageType].documentSelectedTags);
}

export function useStateShowAnnotations() {
    const searchPageType = useSearchPageType();
    return useSelector((state: DefaultRootState) => state.search[searchPageType].showAnnotations);
}

export function useStateHighlightId() {
    const searchPageType = useSearchPageType();
    return useSelector((state: DefaultRootState) => state.search[searchPageType].highlightId);
}

export function useStateResultDetailsTab(): TabType {
    const searchPageType = useSearchPageType();
    return useSelector((state: DefaultRootState) => state.search[searchPageType].resultDetailsTab.openResultTab);
}

export function useStateResultDetailsTabSize(): DynamicSize {
    const searchPageType = useSearchPageType();
    return useSelector((state: DefaultRootState) => state.search[searchPageType].resultDetailsTab.size);
}

export function useStateCurrentPageNumber(): number {
    const searchPageType = useSearchPageType();
    return useSelector((state: DefaultRootState) => {
        return state.search[searchPageType].currentPageNumber;
    });
}

export function useStateCurrentPageRotation(): number {
    const searchPageType = useSearchPageType();
    return useSelector((state: DefaultRootState) => {
        const pageNumber = state.search[searchPageType].currentPageNumber;
        const pageRotation = state.search[searchPageType].pageRotations[pageNumber - 1];
        return pageRotation || 0;
    });
}

export function useStateNumberOfPages(): number {
    const searchPageType = useSearchPageType();
    return useSelector((state: DefaultRootState) => {
        return state.search[searchPageType].numberOfPages;
    });
}

export function useStateScrollTarget(): ClickableElement | null {
    const searchPageType = useSearchPageType();
    return useSelector((state: DefaultRootState) => {
        return state.search[searchPageType].scrollTarget;
    });
}

export function useStateDoResetTransform(): boolean {
    const searchPageType = useSearchPageType();
    return useSelector((state: DefaultRootState) => {
        return state.search[searchPageType].resetTransform;
    });
}

export function useStateSortTypes(): SortType[] | undefined {
    const searchPageType = useSearchPageType();
    return useSelector((state: DefaultRootState) => {
        return state.search[searchPageType].sortTypes;
    });
}

export function useStateIsDocumentLoading() {
    const searchPageType = useSearchPageType();
    return useSelector((state: DefaultRootState) => {
        return state.search[searchPageType].isDocumentLoading;
    });
}

export function useStateUseAutoCorrection() {
    const searchPageType = useSearchPageType();
    return useSelector((state: DefaultRootState) => {
        return state.search[searchPageType].useAutoCorrection;
    });
}

export function useQueryParams() {
    const search = useLocation().search;
    return new URLSearchParams(search);
}

export function useSearchURL() {
    let path = `${window.location.pathname}`;
    path = path.substring(0, path.indexOf(SEARCH_PATH)) + SEARCH_PATH; // ensure that we get any context path like '/app' and always end with '/search'
    return `${window.location.origin}${path}`;
}

/**
 * Synchronizes the URL with the search query in the search bar and vice versa.
 * Every time the search query in the search bar is changed, the URL will be updated.
 * Also, when initially visiting the search page, it will initialize the query from the URL.
 */
export function useSynchronizeSearchFromUrl(searchPageType: SearchPageType, comingFromLandingPage: boolean): void {
    const dispatch = useDispatch<AppDispatch>();
    const container = useStateContainerName();
    const language = getCurrentLanguage();
    const [searchParams, setSearchParams] = useSearchParams();
    const {query: formerUrlSearchExpression} = useParams<{ query: string }>();
    const currentSearchExpression = useStateQuery();
    const uriParams = useQueryParams();
    const searchExpression = uriParams.get(URL_QUERY_PARAM) || currentSearchExpression || formerUrlSearchExpression || '';
    const searchInBookmarks: boolean = isSearchInBookmarks(useStateSources());
    const facetFilterParam: string = uriParams.get(URL_FILTER_PARAM) || '';
    const filtersFromUrl: HibuFacetFilter = parseFiltersFromUrl(facetFilterParam);
    const sortTypesFromConfiguration: SortType[] | undefined = useStateSortTypes();
    const currentSortTypeState: SortType = useCurrentSortType();
    const sortFieldParam: string | null = uriParams.get(URL_SORT_PARAM);
    const currentSortType: SortType = sortFieldParam
        ? parseSortFieldString(sortFieldParam, sortTypesFromConfiguration)
        : currentSortTypeState;
    const defaultSortType: SortType | undefined = useDefaultSortType();
    const spellcheckEnabled = isSpellcheckEnabled(useStateSources());
    const pageParam: string = uriParams.get(URL_PAGE_PARAM) || '';
    const pageNumber: number = parsePageNumberString(pageParam);
    const tabParam: TabType = parseOpenTab(uriParams.get(URL_TAB_PARAM) || '', useStateResultDetailsTab());
    const settingsConfig = useSettingsConfig();
    const allQueryFields = useSelector(
        (state: DefaultRootState) => state.settings.containers[container]?.queryFields ?? [],
    );

    let stateQueryFields = useStateQueryFields();
    stateQueryFields = useStateFieldSpecificSearch() ? stateQueryFields : undefined;

    const retainFilters: boolean = uriParams.get(URL_RF_PARAM) !== 'false'; // null is considered true

    const urlQueryFields = uriParams.get(URL_FIELDS_PARAM);
    const queryFields = resolveQueryFields(stateQueryFields, urlQueryFields, allQueryFields);

    const hasRun = useRef(false); // this is to make sure, useEffect only runs once

    const setRetainFiltersUrl = useCallback(() => {
        if (retainFilters) {
            searchParams.set('rf', 'true');
            setSearchParams(searchParams, {replace: true});
        } else {
            searchParams.set('rf', 'false');
            setSearchParams(searchParams, {replace: true});
        }
    }, [retainFilters, searchParams, setSearchParams]);

    useEffect(() => {
        if (comingFromLandingPage) return;
        if (defaultSortType && !hasRun.current) {
            hasRun.current = true;
            dispatch(startSearchAction(searchPageType, searchExpression));
            dispatch(sortChangeAction(searchPageType, currentSortType));
            dispatch(resetSearchStateParts(searchPageType));
            dispatch(setCurrentPageNumberAction(pageNumber));
            dispatch(switchResultDetailsTabAction(tabParam));
            if (isShowFieldSpecificSearchEnabled(settingsConfig)) {
                dispatch(setQueryFieldsAction(urlQueryFields));
            } else {
                searchParams.delete(URL_FIELDS_PARAM);
                setSearchParams(searchParams, {replace: true});
            }
            dispatch(changeFilterPersistence(retainFilters));
            setRetainFiltersUrl();
            dispatch(updateAllFilters(filtersFromUrl));
            const sort: HibuSearchRequestSort = getSort(searchExpression, currentSortType, defaultSortType);
            const request = createSearchRequestFromUrl(
                container,
                language,
                searchExpression,
                searchInBookmarks,
                filtersFromUrl,
                queryFields,
                sort,
                spellcheckEnabled
            );
            dispatch(doFetchFacets(searchPageType, request));
            dispatch(doSearchRequest(request, true, true))?.then((result: SearchCompletedAction | undefined) => {
                //if id filter is present in url, then get the current document (only one) and expand it
                if (filtersFromUrl['id']) {
                    const docs = result?.searchResult.docs;
                    const doc = (docs ? docs[0] : null) as ProdDocument | undefined;
                    dispatch(fetchDocumentDetails(doc));
                }
            });
        }
    }, [allQueryFields, comingFromLandingPage, container, currentSortType, defaultSortType, dispatch, filtersFromUrl, language, pageNumber, queryFields, retainFilters, searchExpression, searchInBookmarks, searchPageType, searchParams, setRetainFiltersUrl, setSearchParams, settingsConfig, settingsConfig.enabled, settingsConfig.toggles.showFieldSpecificSearch, spellcheckEnabled, tabParam, urlQueryFields]);
}

export function resolveQueryFields(
    stateQueryFields: string[] | undefined,
    urlQueryFields: string | null,
    allQueryFields: string[],
): string[] | undefined {
    if (urlQueryFields === null) {
        return stateQueryFields;
    }
    if (urlQueryFields === QUERY_FIELDS_ALL) {
        return allQueryFields;
    }
    return urlQueryFields
        .split(QUERY_FIELDS_SEPARATOR)
        .filter((queryField) => queryField !== '')
        .filter((queryField) => allQueryFields.includes(queryField));
}

function createSearchRequestFromUrl(
    container: string,
    language: string,
    searchExpression: string,
    searchInBookmarks: boolean,
    filter: HibuFacetFilter,
    queryFields: string[] | undefined,
    sort: HibuSearchRequestSort,
    spellcheckEnabled: boolean
): SearchRequest {
    return {
        container: container,
        displayLanguage: language,
        query: searchExpression,
        from: 0,
        size: SEARCH_BATCH_SIZE,
        filters: filter,
        queryFields: queryFields,
        sort: sort,
        spellcheckEnabled: spellcheckEnabled,
        spellcheckParams: getSpellcheckParams(true),
        isSearchInBookmarks: searchInBookmarks
    };
}

export function useSettingsContainerRequest(): void {
    const dispatch = useDispatch();
    const containerName = useSelector((state: DefaultRootState) => state.settings.containerName);
    const searchPageType = useSearchPageType();
    useEffect(() => {
        dispatch(doLoadContainersRequest(searchPageType, containerName));
    }, [containerName, dispatch, searchPageType]);
}

export function useLoadVersionsRequest(callback: (response: ContainersResponse) => void): void {
    const dispatch = useDispatch();
    useEffect(() => {
        dispatch(doGetAppVersionsRequest(callback));
    }, [callback, dispatch]);
}

export function useStateChat(): ActiveDrawerContent {
    const searchPageType = useSearchPageType();
    return useSelector((state: DefaultRootState) => {
        return state.search[searchPageType].activeDrawerContent;
    });
}
